import React, { useEffect, useRef } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';

const PROGRAMIZ = {
  border: '#E4E7EB',
  headerBg: '#F8F9FA',
  bodyBg: '#FFFFFF',
  title: '#1F2937',
  text: '#1F2937',
  muted: '#9CA3AF',
  input: '#2563EB',
  error: '#DC2626',
  accent: '#2563EB',
};

export default function EditorConsolePanel({
  output = '',
  error = '',
  running = false,
  open = true,
  stdin = '',
  onStdinChange,
  readOnly = false,
  showStdin = true,
  stdinHelperText = '',
  stdinHintText = '',
  stdinMaxRows = 2,
  outputMinHeight = 120,
  outputMaxHeight = 280,
  interactive = false,
  transcript = [],
  waitingForInput = false,
  currentInput = '',
  onCurrentInputChange,
  onSubmitInput,
  variant = 'default',
  onClear,
  fillHeight = false,
}) {
  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const isProgramiz = variant === 'programiz';
  const hasContent = interactive
    ? transcript.length > 0 || Boolean(error)
    : Boolean(output || error);

  useEffect(() => {
    const el = outputRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [output, error, running, transcript, waitingForInput, currentInput]);

  useEffect(() => {
    if (interactive && waitingForInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [interactive, waitingForInput]);

  const inputPlaceholder =
    stdinHintText ||
    (stdinHelperText && !/optional/i.test(stdinHelperText) ? stdinHelperText : '') ||
    (interactive ? 'Type your answer and press Enter...' : 'Type input here if your program needs it, then Run...');

  const stdinRows = Math.max(1, Math.min(stdinMaxRows, 4));

  const handleInteractiveKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const value = String(currentInput ?? '').trimEnd();
    if (!value.trim() && waitingForInput) return;
    onSubmitInput?.(value);
  };

  const renderInteractiveTranscript = () => (
    <Box
      component="pre"
      sx={{
        m: 0,
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        color: isProgramiz ? PROGRAMIZ.text : '#D4D4D4',
      }}
    >
      {transcript.map((entry, index) => {
        if (entry.type === 'in') {
          return (
            <Box
              key={`${index}-in`}
              component="span"
              sx={{ display: 'block', color: isProgramiz ? PROGRAMIZ.input : '#9CDCFE' }}
            >
              {entry.text}
            </Box>
          );
        }
        return (
          <Box
            key={`${index}-out`}
            component="span"
            sx={{ color: isProgramiz ? PROGRAMIZ.text : '#D4D4D4' }}
          >
            {entry.text}
          </Box>
        );
      })}
      {waitingForInput && !readOnly ? (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            minWidth: 120,
            width: '100%',
          }}
        >
          <Box
            component="input"
            ref={inputRef}
            value={currentInput}
            onChange={(event) => onCurrentInputChange?.(event.target.value)}
            onKeyDown={handleInteractiveKeyDown}
            disabled={running}
            placeholder={inputPlaceholder}
            spellCheck={false}
            sx={{
              flex: 1,
              bgcolor: 'transparent',
              color: isProgramiz ? PROGRAMIZ.input : '#9CDCFE',
              border: 'none',
              borderRadius: 0,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: 1.6,
              p: 0,
              m: 0,
              outline: 'none',
              minWidth: 80,
              '&::placeholder': { color: isProgramiz ? PROGRAMIZ.muted : '#5A5A5A' },
            }}
          />
        </Box>
      ) : null}
    </Box>
  );

  const headerTitle = isProgramiz ? 'Output' : 'TERMINAL';

  const panelSx = isProgramiz
    ? {
        border: 'none',
        bgcolor: PROGRAMIZ.bodyBg,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: fillHeight ? 1 : 'none',
        minHeight: fillHeight ? 0 : outputMinHeight,
        height: fillHeight ? '100%' : 'auto',
      }
    : {
        border: '1px solid #3C3C3C',
        borderTop: 'none',
        bgcolor: '#1E1E1E',
        overflow: 'hidden',
      };

  const headerSx = isProgramiz
    ? {
        px: 2,
        py: 0,
        minHeight: 44,
        bgcolor: PROGRAMIZ.headerBg,
        borderBottom: `1px solid ${PROGRAMIZ.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }
    : {
        px: 1.5,
        py: 0.75,
        bgcolor: '#252526',
        borderBottom: '1px solid #3C3C3C',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      };

  const bodySx = isProgramiz
    ? {
        display: 'flex',
        flexDirection: 'column',
        bgcolor: PROGRAMIZ.bodyBg,
        flex: fillHeight ? 1 : 'none',
        minHeight: fillHeight ? 0 : outputMinHeight,
        maxHeight: fillHeight ? 'none' : outputMaxHeight,
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0D0D0D',
        minHeight: outputMinHeight,
        maxHeight: interactive
          ? outputMaxHeight
          : outputMaxHeight + (showStdin && !readOnly ? 48 : 0),
      };

  const outputAreaSx = isProgramiz
    ? {
        flex: 1,
        overflow: 'auto',
        overflowX: 'auto',
        p: { xs: 1.5, sm: 2 },
        fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
        fontSize: { xs: 13, sm: 14 },
        lineHeight: 1.6,
        color: PROGRAMIZ.text,
        wordBreak: 'break-word',
        bgcolor: PROGRAMIZ.bodyBg,
      }
    : {
        flex: 1,
        overflow: 'auto',
        overflowX: 'auto',
        p: { xs: 1, sm: 1.5 },
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: { xs: 12, sm: 13 },
        lineHeight: 1.6,
        color: '#D4D4D4',
        wordBreak: 'break-word',
        animation: running ? 'pulse 1.2s ease-in-out infinite' : 'none',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.7 },
        },
      };

  return (
    <Box sx={panelSx}>
      <Box sx={headerSx}>
        <Typography
          variant="body2"
          sx={{
            color: isProgramiz ? PROGRAMIZ.title : '#CCCCCC',
            fontWeight: isProgramiz ? 500 : 600,
            fontSize: isProgramiz ? 14 : undefined,
          }}
        >
          {headerTitle}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isProgramiz && running ? (
            <Typography variant="caption" sx={{ color: '#007ACC' }}>
              Running...
            </Typography>
          ) : null}
          {!isProgramiz && interactive && waitingForInput && !running ? (
            <Typography variant="caption" sx={{ color: '#4EC9B0' }}>
              Waiting for input...
            </Typography>
          ) : null}
          {isProgramiz && onClear ? (
            <Button
              size="small"
              onClick={onClear}
              disabled={running || (!hasContent && !waitingForInput)}
              sx={{
                color: PROGRAMIZ.accent,
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'none',
                minWidth: 0,
                px: 1,
                '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)' },
                '&.Mui-disabled': { color: PROGRAMIZ.muted },
              }}
            >
              Clear
            </Button>
          ) : null}
        </Box>
      </Box>

      <Collapse in={open} timeout={300} sx={fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
        <Box sx={bodySx}>
          <Box ref={outputRef} sx={outputAreaSx}>
            {error ? (
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  color: isProgramiz ? PROGRAMIZ.error : '#F48771',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                }}
              >
                {error}
              </Typography>
            ) : null}

            {interactive ? (
              renderInteractiveTranscript()
            ) : (
              <>
                {output ? (
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      color: isProgramiz ? PROGRAMIZ.text : '#D4D4D4',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                    }}
                  >
                    {output}
                  </Typography>
                ) : null}
                {!hasContent && !running ? (
                  <Typography variant="body2" sx={{ color: isProgramiz ? PROGRAMIZ.muted : '#858585' }}>
                    {isProgramiz ? '' : 'Run your code to see output here.'}
                  </Typography>
                ) : null}
              </>
            )}

            {interactive && !hasContent && !running && !waitingForInput ? (
              <Typography variant="body2" sx={{ color: isProgramiz ? PROGRAMIZ.muted : '#858585' }}>
                {isProgramiz ? '' : 'Click Run — if your program asks for input, type your answer here when prompted.'}
              </Typography>
            ) : null}
          </Box>

          {!interactive && showStdin && !readOnly ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                borderTop: `1px solid ${isProgramiz ? PROGRAMIZ.border : '#3C3C3C'}`,
                px: 1,
                py: 0.75,
                flexShrink: 0,
                bgcolor: isProgramiz ? PROGRAMIZ.bodyBg : 'transparent',
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: isProgramiz ? PROGRAMIZ.accent : '#4EC9B0',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  lineHeight: 1.6,
                  pt: 0.25,
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                ›
              </Typography>
              <Box
                component="textarea"
                value={stdin}
                onChange={(e) => onStdinChange?.(e.target.value)}
                rows={stdinRows}
                placeholder={inputPlaceholder}
                spellCheck={false}
                sx={{
                  flex: 1,
                  resize: 'none',
                  bgcolor: 'transparent',
                  color: isProgramiz ? PROGRAMIZ.text : '#D4D4D4',
                  border: 'none',
                  borderRadius: 0,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  lineHeight: 1.6,
                  p: 0,
                  outline: 'none',
                  boxSizing: 'border-box',
                  '&::placeholder': { color: isProgramiz ? PROGRAMIZ.muted : '#5A5A5A' },
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
